import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import AppContext from '../contexts/AppContext';
import AppDivisionContext from '../contexts/AppDivisionContext';
import NotificationContext from '../contexts/NotificationsContext';
import { useOffline } from '../contexts/OfflineContext';
import inspectionApi from '../utils/api/inspection';
import { useTenantRedirect } from './useTenantRedirect';

export interface INotification {
  id: number;
  title: string;
  content: string;
  date: string;
  link: string;
  is_opened: boolean;
  /** SW2-1651 — null on notifications raised before tenant tagging */
  tenantId?: number | null;
  tenantName?: string | null;
  divisionId?: number | null;
}

/**
 * A notification link may only be an app-relative path.
 *
 * Cross-tenant navigation hands the link to the destination tenant as
 * `/identity-auth?to=<link>`, and `IdentityAuth` passes that straight into
 * `<Redirect>`. A protocol-relative value such as `//example.com` would
 * therefore navigate off-site, so it is rejected here.
 */
export const isSafeNotificationPath = (link?: string | null): boolean =>
  typeof link === 'string' && /^\/(?![/\\])/.test(link);

const isAbsoluteLink = (link?: string | null): boolean =>
  typeof link === 'string' && /^https:\/\/\S*/.test(link);

// Notification links are backend-generated (notification_types.link_template)
// and come in three shapes: division-scoped (`/divisions/3/projects/7442`),
// inspection links carrying the division in the query string
// (`/inspection/398444/questionnaire?divisionId=3`), and links with no division
// at all (`/projects/3288/inspections`, `/dashboard`).
const DIVISION_PATH = /^\/divisions\/(\d+)(?:\/|$)/;
const INSPECTION_PATH = /^\/inspection\/(\d+)(?:\/|$)/;

/**
 * Owns every way a notification row can navigate, so the popper and the expanded
 * modal cannot drift apart.
 *
 * Returns `mfaModal` because switching tenant may demand MFA, and the modal has
 * to be rendered by whichever component hosts the list.
 */
export const useNotificationNavigate = () => {
  const history = useHistory();
  const appContext = useContext(AppContext);
  const { getPath, saveAppDivisionId } = useContext(AppDivisionContext);
  const notificationContext = useContext(NotificationContext);
  const isOffline = useOffline();

  const {
    redirectToTenant,
    showMFAModal,
    mfaData,
    handleMFAVerificationSuccess,
    handleMFAModalClose,
    authStore,
  } = useTenantRedirect();

  const user = appContext.get('user');
  const currentTenantId =
    (user && (user.tenantId || user.userTenantRoles?.[0]?.tenant_id)) || null;

  /**
   * Point the app at the division that owns the notification target, so the
   * route we just pushed resolves against the right division. Used only when the
   * backend could not tell us the division itself (SW2-2180).
   *
   * Note the division id must come from `link` (the raw link), never from
   * `getPath(link)` — `getPath` prefixes division-scoped routes with the
   * *current* division, which is precisely the value we are trying to replace.
   */
  const syncAppDivision = (link: string) => {
    const [pathname, search] = link.split('?');

    const queryDivisionId = Number(new URLSearchParams(search).get('divisionId'));

    if (queryDivisionId) {
      saveAppDivisionId(queryDivisionId);

      return;
    }

    const divisionMatch = pathname.match(DIVISION_PATH);

    if (divisionMatch) {
      saveAppDivisionId(Number(divisionMatch[1]));

      return;
    }

    const inspectionMatch = pathname.match(INSPECTION_PATH);

    if (inspectionMatch) {
      inspectionApi
        .getInspectionLinkedAllData(inspectionMatch[1])
        .then((res: any) => {
          // getInspectionLinkedAllData swallows errors and resolves undefined,
          // and an inspection is not guaranteed to carry a linked project.
          const divisionId = res?.project?.divisionProject?.division_id;

          if (divisionId) {
            saveAppDivisionId(divisionId);
          }
        });
    }
  };

  const navigate = (notification: INotification) => {
    if (isOffline) {
      return;
    }

    const { id, link, tenantId, divisionId } = notification;

    notificationContext.markNotificationAsRead(id);

    if (isAbsoluteLink(link)) {
      window.location.href = link;

      return;
    }

    if (!isSafeNotificationPath(link)) {
      return;
    }

    const belongsToAnotherTenant =
      !!tenantId && !!currentTenantId && tenantId !== currentTenantId;

    if (belongsToAnotherTenant) {
      /**
       * `getPath` decides whether the route needs a division segment at all, so
       * reuse it with the destination's division rather than this tenant's.
       * An unknown division falls back to 1, which `DivisionRoute` corrects on
       * arrival.
       */
      redirectToTenant(tenantId as number, getPath(link, divisionId || 1));

      return;
    }

    history.push(getPath(link, divisionId || undefined));

    /**
     * The app division has to be moved as well as the path. `getPath` only
     * builds a path, and for a non-division route such as `/inspection/...` the
     * division never appears in the URL for `DivisionRoute` to pick it up — so
     * without this the user lands on another division's record while the app is
     * still pointed at the previous one.
     */
    if (divisionId) {
      saveAppDivisionId(divisionId);
    } else {
      syncAppDivision(link);
    }
  };

  return {
    navigate,
    mfaModal: {
      show: showMFAModal,
      email: mfaData?.email || '',
      handleClose: handleMFAModalClose,
      onVerificationSuccess: handleMFAVerificationSuccess,
      sendVerificationCode: authStore.sendVerificationCode,
      verifyCode: authStore.verifyCode,
      hasData: !!mfaData,
    },
  };
};

export default useNotificationNavigate;
